<template>

  <SubHeading :home="home" :tabs="tabs" :currTab="currTab" @active="handleActive" />

  <!-- search layer -->
  <!-- <div class="grey lighten-4">
    <div class="container section"></div>
  </div> -->

  <!-- breadcrumbs -->
  <nav class="sba-collection-breadcrumb z-depth-0">
    <div class="container nav-wrapper">
      <div class="sba-collection-breadcrumb-row">
        <router-link to="/" class="breadcrumb sba-collection-crumb teal-text">
          <i class="material-icons">home</i>
          <span>Dashboard</span>
        </router-link>
        <i class="material-icons sba-collection-crumb-separator">chevron_right</i>
        <router-link to="/provider" class="breadcrumb sba-collection-crumb blue-grey-text text-darken-3">
          <span>Providers Home</span>
        </router-link>
        <i class="material-icons sba-collection-crumb-separator">chevron_right</i>
        <span class="breadcrumb sba-collection-crumb blue-grey-text pointable">
          <span>Sync</span>
        </span>
        <span class="sba-collection-context">Provider Sync</span>
      </div>
    </div>
  </nav>

  <!-- Main -->
  <div class="sba-collection-page-surface is-workbench provider-sync-page-surface">
  <div class="container">

    <!-- Providers -->
    <LoadingDiv v-if="!isReady" />

    <div class="provider-sync-workbench sba-slide-in" v-else>
      <section class="provider-sync-head">
        <div class="provider-sync-title-block">
          <label class="active teal-text">Provider Sync</label>
          <h5>Provider Sync</h5>
          <p>Track provider registrations awaiting oneProvider approval, then match approved providers into EasyHMO.</p>
        </div>
      </section>

      <section class="provider-sync-body">
        <aside class="provider-sync-list-panel">
          <div class="provider-sync-list-head">
            <span>Waiting oneProvider approval</span>
            <small>{{ pendingRegistrationTotal }} pending</small>
          </div>

          <div class="provider-registration-list" v-if="pendingRegistrations.length">
            <div
              v-for="registration in pendingRegistrations"
              :key="registration.id"
              class="provider-registration-item"
            >
              <strong>{{ registration.submitted_name }}</strong>
              <span>{{ registrationStatusText(registration.status) }}</span>
              <small>{{ registration.submitted_user_email || registration.submitted_email || 'No email recorded' }}</small>
            </div>
          </div>

          <div class="provider-registration-empty" v-else>
            <i class="material-icons">hourglass_empty</i>
            <span>No provider is waiting on oneProvider approval.</span>
          </div>

          <div class="provider-sync-section-divider" aria-hidden="true"></div>

          <div class="provider-sync-list-head">
            <span>Ready for payer matching</span>
            <small>{{ pendingProviderTotal }} pending</small>
          </div>

          <ol class="provider-sync-list" :start="providerListStart">
            <li v-for="provider in providers" :key="provider.id">
              <a href="#" @click.prevent="handleSync(provider)">{{ provider.name }}</a>
              <span v-if="provider.pivot.status === 'a'">Done</span>
            </li>
          </ol>

          <div class="provider-sync-empty center" v-if="!providers.length">
            <i class="material-icons">sync_disabled</i>
            <h6>No provider found</h6>
            <p>There are no approved oneProvider records waiting for payer matching.</p>
          </div>

          <PaginationDiv :payLoad="allPages" @newPage="handlePage" v-if="showPagination" />
        </aside>

        <section class="provider-sync-detail-panel">
          <SyncProvider :oneProvider="provider" @clear="handleClear" @update="handleUpdate" v-if="provider" />
          
          <div class="provider-sync-placeholder" :class="{ 'is-loading-provider': isProviderLoading }" v-else>
            <div v-if="!isProviderLoading">
              <i class="material-icons">medical_services</i>
              <h5>Select Provider</h5>
              <p>Click an approved provider from the ready list to match it with the EasyHMO provider record.</p>
            </div>
            <div v-else>
              <i class="material-icons">sync</i>
              <h5>Selecting Provider for Syncing</h5>
              <p>Please wait while provider details are loading.</p>
            </div>
          </div>

          <!-- isProviderLoading -->
          <div class="progress provider-sync-progress" v-if="isProviderLoading">
            <div class="indeterminate"></div>
          </div>
        </section>
      </section>
    </div>

  </div>
  </div>
</template>

<script>
import { computed, onMounted, ref } from "vue";
import { useRouter } from "vue-router";
import getItem from "@/composables/getItem";
import getItems from "@/composables/getItems";
import SubHeading from "@/components/HeaderSubDiv.vue";
import SyncProvider from "@/components/provider/SyncProviderComponent.vue";
import LoadingDiv from "@/components/LoadingDiv.vue";
import PaginationDiv from "@/components/pager/PaginationDiv.vue";
import { siloMode } from '@/laravel/config';


export default {
  name: "providerSync",
  components: { SubHeading, SyncProvider, LoadingDiv, PaginationDiv },
  setup() {
    const response = ref(null);
    const registrationResponse = ref(null);
    const isReady = ref(false);
    const isLoading = ref(false);
    const isProviderLoading = ref(false);
    const currUrl = ref(null);
    const currHook = ref(null);
    const error = ref(null);
    const provider = ref(null);
    const searchItem = ref(null);
    const home = ref(null);
    const currTab = ref(null);
    const tabs = ref([]);

    // vue router
    const router = useRouter();

    // start
    onMounted(() => {
      // page name
      document.title = "Sync Providers | EasyHMO";
    });

    // checker
    onMounted(() => {
      // check silo
      if (siloMode) {
        // redirect to home
        router.push({ name: "providerHome" });
      }
    });

    // menu
    onMounted(() => {
      // load tab
      tabs.value.push({ id: 1, name: "Providers", title: "All", icon: "arrow_back", url: "provider", hash: null, });
      tabs.value.push({ id: 2, name: "Providers", title: "View", icon: "visibility", url: "provider", hash: "#view", });
      tabs.value.push({ id: 3, name: "Providers", title: "Add Provider", icon: "add_circle", url: "providerAdd", hash: null, });
      tabs.value.push({ id: 4, name: "Providers", title: "Sync", icon: null, url: null, hash: null, });

      // set default
      currTab.value = tabs.value.find(x => x.title.includes("Sync"));
      // set home
      home.value = tabs.value.find(x => x.id === 1);
    });

    // handle curr status
    const handleActive = (tab) => {
      // refer
      if (tab.hash) {
        router.push({ name: tab.url, hash: tab.hash });
      } else {
        router.push({ name: tab.url });
      }
    };

    // numbering
    const numberBadge = (num) => {
      return (
        (allPages.value.current_page - 1) * allPages.value.per_page + num + 1
      );
    };

    // get function
    const getItemsFromApi = (table) => {
      (async () => {
        const { res, err } = await getItems(table);

        if (res.value) {
          response.value = res.value.data;
        } else {
          error.value = err.value;
        }

        // page is ready, loading is complete
        isReady.value = true;
        isLoading.value = false;
      })();
    };

    const getPendingRegistrationsFromApi = () => {
      (async () => {
        const { res, err } = await getItems("integration/oneprovider/provider-sync/registrations/pending");

        if (res.value) {
          registrationResponse.value = res.value.data;
        } else {
          error.value = err.value;
        }
      })();
    };

    // starter
    onMounted(() => {
      // set current Url
      currUrl.value = "integration/oneprovider/provider-sync/pending";
      // set current Hook
      currHook.value = "?";
      // get items from function
      getItemsFromApi(currUrl.value);
      getPendingRegistrationsFromApi();
    });

    // clear
    const handleClear = (val) => {
      if (val) {
       // clear provider
       provider.value = null;
      }
    }

    // clear
    const handleUpdate = (val) => {
      if (val) {
       // update provider
       response.value.data.forEach((x) => {
        if (x.id === val.id) {
          // get all
          x.pivot.status = val;
        }
       });
      }
    }

    const scrollToPageTop = () => {
      window.scrollTo({ top: 0, behavior: "smooth" });
    };

    // sync
    const handleSync = (val) => {
      scrollToPageTop();

      // start
      provider.value = null;
      // start loading
      isProviderLoading.value = true;

      // confirm availabity again
      (async () => {
        const { res, err } = await getItem("integration/oneprovider/provider-sync/providers", val.opid);

        if (res.value) {
          provider.value = res.value.data;
        } else {
          error.value = err.value;
        }

        // end
        isProviderLoading.value = false;

      })();
    }

    // switch page
    const handlePage = (page) => {
      if (Number(page) === Number(allPages.value.current_page)) {
        return;
      }

      window.scrollTo({ top: 0, behavior: "smooth" });
      // start laoding
      isLoading.value = true;
      // get new page items from function
      getItemsFromApi(`${currUrl.value}${currHook.value}page=${page}`); //
    };

    // output results
    const providers = computed(() => {
      if (!response.value) {
        return [];
      } else {
        return response.value.data;
      }
    });

    // output pages
    const allPages = computed(() => {
      if (!response.value) {
        return [];
      } else {
        return response.value; // get all
      }
    });

    const pendingRegistrations = computed(() => {
      if (!registrationResponse.value) {
        return [];
      } else {
        return registrationResponse.value.data || [];
      }
    });

    const pendingProviderTotal = computed(() => {
      if (!response.value) {
        return 0;
      } else {
        return response.value.total ?? providers.value.length;
      }
    });

    const pendingRegistrationTotal = computed(() => {
      if (!registrationResponse.value) {
        return 0;
      } else {
        return registrationResponse.value.total ?? pendingRegistrations.value.length;
      }
    });

    const registrationStatusText = (status) => {
      switch (status) {
        case "needs_duplicate_review":
          return "Duplicate review";
        case "pending_super_admin_review":
          return "Identity review";
        default:
          return "Pending approval";
      }
    };

    const providerListStart = computed(() => {
      if (!response.value) {
        return 1;
      } else {
        return ((response.value.current_page ?? 1) - 1) * (response.value.per_page ?? providers.value.length) + 1;
      }
    });

    const showPagination = computed(() => {
      if (!response.value) {
        return false;
      } else {
        return (response.value.total ?? 0) > (response.value.per_page ?? providers.value.length);
      }
    });

    return {
      handleClear,
      handleUpdate,
      handleActive,
      home,
      currTab,
      tabs,
      numberBadge,
      handlePage,
      handleSync,
      isReady,
      isLoading,
      isProviderLoading,
      error,
      provider,
      searchItem,
      providers,
      pendingRegistrations,
      allPages,
      pendingProviderTotal,
      pendingRegistrationTotal,
      registrationStatusText,
      providerListStart,
      showPagination,
    };
  },
};
</script>

<style scoped>
.provider-sync-workbench {
  color: #263238;
}

.provider-sync-head {
  background: #ffffff;
  border: 1px solid #c4d3d9;
  border-radius: 6px 6px 0 0;
  box-shadow: 0 12px 20px -18px rgba(38, 50, 56, 0.55);
  padding: 24px 26px;
  position: relative;
}

.provider-sync-head::before {
  border: 1px solid #c4d3d9;
  border-bottom: 0;
  border-radius: 7px 7px 0 0;
  content: "";
  height: 18px;
  left: -1px;
  pointer-events: none;
  position: absolute;
  right: -1px;
  top: -1px;
  z-index: 1;
}

.provider-sync-title-block {
  position: relative;
  z-index: 2;
}

.provider-sync-title-block h5,
.provider-sync-placeholder h5 {
  color: #263238;
  font-weight: 700;
  margin: 4px 0 0;
}

.provider-sync-title-block p {
  color: #607d8b;
  line-height: 1.45;
  margin: 6px 0 0;
  max-width: 780px;
}

.provider-sync-body {
  background: #f7f9f9;
  border: 1px solid #c4d3d9;
  border-radius: 0 0 6px 6px;
  border-top: 0;
  box-shadow: 0 18px 34px -31px rgba(38, 50, 56, 0.75);
  display: grid;
  gap: 16px;
  grid-template-columns: minmax(220px, 280px) minmax(0, 1fr);
  padding: 18px;
}

.provider-sync-list-panel,
.provider-sync-detail-panel {
  background: #ffffff;
  border: 1px solid #d7e1e5;
  border-radius: 6px;
  min-width: 0;
}

.provider-sync-list-panel {
  padding: 16px;
}

.provider-sync-section-divider {
  border-top: 1px solid #e1e8eb;
  margin: 16px 0;
}

.provider-sync-list-head {
  align-items: baseline;
  border-bottom: 1px solid #e1e8eb;
  display: flex;
  justify-content: space-between;
  padding-bottom: 12px;
}

.provider-sync-list-head span {
  color: #263238;
  font-size: 0.9rem;
  font-weight: 800;
  text-transform: uppercase;
}

.provider-sync-list-head small {
  color: #607d8b;
  font-weight: 700;
}

.provider-sync-list {
  margin: 12px 0 0 20px;
}

.provider-sync-list li {
  border-bottom: 1px solid #eef3f5;
  color: #607d8b;
  padding: 9px 0;
}

.provider-sync-list li:last-child {
  border-bottom: 0;
}

.provider-sync-list a {
  color: #00695c;
  font-weight: 700;
}

.provider-sync-list span {
  background: #e8f5e9;
  border-radius: 999px;
  color: #2e7d32;
  display: inline-block;
  font-size: 0.72rem;
  font-weight: 800;
  margin-left: 6px;
  padding: 2px 8px;
  text-transform: uppercase;
}

.provider-registration-list {
  display: grid;
  gap: 8px;
  margin-top: 12px;
}

.provider-registration-item {
  border: 1px solid #e6ecef;
  border-left: 3px solid #f9a825;
  border-radius: 4px;
  background: #fffdf7;
  padding: 10px 11px;
}

.provider-registration-item strong,
.provider-registration-item span,
.provider-registration-item small {
  display: block;
}

.provider-registration-item strong {
  color: #263238;
  font-size: 0.95rem;
  font-weight: 800;
}

.provider-registration-item span {
  color: #8a5a00;
  font-size: 0.78rem;
  font-weight: 900;
  margin-top: 3px;
  text-transform: uppercase;
}

.provider-registration-item small {
  color: #607d8b;
  font-size: 0.78rem;
  font-weight: 600;
  line-height: 1.35;
  margin-top: 5px;
  overflow-wrap: anywhere;
}

.provider-registration-empty {
  align-items: center;
  border: 1px dashed #d7e1e5;
  border-radius: 4px;
  color: #607d8b;
  display: flex;
  gap: 8px;
  margin-top: 12px;
  padding: 12px;
}

.provider-registration-empty i {
  color: #90a4ae;
  font-size: 20px;
}

.provider-registration-empty span {
  font-size: 0.86rem;
  font-weight: 650;
  line-height: 1.35;
}

.provider-sync-detail-panel {
  padding: 18px;
  position: relative;
}

.provider-sync-placeholder {
  align-items: center;
  background: #f8fafb;
  border: 1px solid #e1e8eb;
  border-radius: 6px;
  color: #607d8b;
  display: flex;
  justify-content: center;
  min-height: 280px;
  padding: 24px;
  text-align: center;
}

.provider-sync-placeholder i {
  color: #00897b;
  font-size: 32px;
}

.provider-sync-placeholder p,
.provider-sync-empty p {
  color: #607d8b;
  line-height: 1.5;
  margin: 8px auto 0;
  max-width: 460px;
}

.provider-sync-empty {
  color: #607d8b;
  padding: 28px 10px 10px;
}

.provider-sync-empty i {
  color: #90a4ae;
  font-size: 28px;
}

.provider-sync-placeholder.is-loading-provider {
  border-bottom-left-radius: 0;
  border-bottom-right-radius: 0;
}

.provider-sync-progress {
  background-color: #edf4f5;
  border-radius: 0 0 6px 6px;
  height: 4px;
  margin: 0;
}

.provider-sync-progress .indeterminate {
  background-color: #00897b;
}

.provider-sync-detail-panel :deep(.card),
.provider-sync-detail-panel :deep(.card-panel) {
  border: 1px solid #e1e8eb;
  border-radius: 6px;
  box-shadow: none;
}

@media only screen and (max-width: 992px) {
  .provider-sync-body {
    grid-template-columns: 1fr;
  }

  .provider-sync-detail-panel {
    order: 1;
  }

  .provider-sync-list-panel {
    order: 2;
  }
}

@media only screen and (max-width: 600px) {
  .provider-sync-head,
  .provider-sync-body,
  .provider-sync-detail-panel {
    padding: 16px;
  }

  .provider-sync-title-block h5,
  .provider-sync-placeholder h5 {
    font-size: 1.45rem;
  }
}
</style>
